// Armor sets (CHANGELOG 0.2.0, section 3). A set is made of the five armor slots:
// helmet, body, gloves, pants and boots. Bonuses stack: 3 pieces give the 2- and
// the 3-piece bonus. Rarity does not matter, Common pieces count too, and two sets
// can be active at once (for example 2 + 3 pieces).

import { findItemTemplate } from '../content/items';
import { wornCounts } from './durability';
import { ARMOR_SLOTS, SET_IDS, type Equipment, type SetHookId, type SetId } from '../types';

export const SET_THRESHOLDS = [2, 3, 5] as const;
export type SetThreshold = (typeof SET_THRESHOLDS)[number];

/** Plain numbers a set bonus adds. Percentages are fractions (0.05 = 5%). */
export interface SetStatMods {
  escape?: number;
  agi?: number;
  /** Dodge chance (0.05 = 5%). */
  dodge?: number;
  capacity?: number;
  craftSpeed?: number;
  /** Flat defense. */
  defense?: number;
  /** Extra scrap when dismantling. */
  dismantleScrap?: number;
  healing?: number;
  /** Heal this share of max HP after every fight. */
  postFightHeal?: number;
  /** Harder to detect (adds to stealth). */
  stealth?: number;
  /** Extra damage on the ambush strike. */
  ambush?: number;
  /** Attack multiplier bonus. */
  attackPct?: number;
  /** Cheaper repairs. */
  repairDiscount?: number;
  /** Shorter expeditions (0.1 = −10% time). */
  expeditionTime?: number;
  /** Flat PER. */
  per?: number;
  /** Damage against Mutants (0.1 = +10%). */
  vsMutant?: number;
  /** 11. 10. 2026 (Foundry): less damage from melee hits (0.05 = −5%), with the affixes at most 50%. */
  meleeResist?: number;
}

export interface SetBonus {
  text: string;
  stats?: SetStatMods;
  hook?: SetHookId;
}

export interface SetDef {
  id: SetId;
  name: string;
  bonuses: Record<SetThreshold, SetBonus[]>;
  /**
   * The set mark on gear (26. 9. 2026): a corner in `color` with `letter` (in `ink` while the
   * set gives a bonus). Colours stay clear of the rarity colours; the letter tells sets apart
   * without colour.
   */
  mark: { letter: string; color: string; ink: string };
}

export const SETS: Record<SetId, SetDef> = {
  drifter: {
    id: 'drifter',
    name: 'Drifter',
    mark: { letter: 'D', color: '#C4A574', ink: '#14130F' },
    bonuses: {
      2: [{ text: '+5% escape chance', stats: { escape: 0.05 } }],
      3: [{ text: '+2 AGI and +5% dodge', stats: { agi: 2, dodge: 0.05 } }],
      5: [{ text: 'The first escape of every expedition always works', hook: 'drifter_first_escape' }],
    },
  },
  worker: {
    id: 'worker',
    name: 'Worker',
    mark: { letter: 'W', color: '#E3D04F', ink: '#14130F' },
    bonuses: {
      2: [{ text: '+10 capacity', stats: { capacity: 10 } }],
      3: [{ text: '+10% crafting speed', stats: { craftSpeed: 0.1 } }],
      5: [{ text: '−20% repair cost', stats: { repairDiscount: 0.2 } }],
    },
  },
  scrapper: {
    id: 'scrapper',
    name: 'Scrapper',
    mark: { letter: 'S', color: '#B0703F', ink: '#14130F' },
    bonuses: {
      2: [{ text: '+5 defense', stats: { defense: 5 } }],
      3: [{ text: '+10% scrap from dismantling', stats: { dismantleScrap: 0.1 } }],
      5: [{ text: '15% chance to return 20% of melee damage to the attacker', hook: 'scrapper_reflect' }],
    },
  },
  // Krug 5 (F10), from the Item Catalog.
  rider: {
    id: 'rider',
    name: 'Rider',
    mark: { letter: 'R', color: '#8E4A52', ink: '#F3EEDF' },
    bonuses: {
      2: [{ text: '+5% dodge', stats: { dodge: 0.05 } }],
      3: [{ text: '−10% expedition time', stats: { expeditionTime: 0.1 } }],
      5: [{ text: 'After a successful escape, the one you ran from cannot follow you', hook: 'rider_no_follow' }],
    },
  },
  hunter: {
    id: 'hunter',
    name: 'Hunter',
    mark: { letter: 'H', color: '#4F7F6A', ink: '#F3EEDF' },
    bonuses: {
      2: [{ text: '+3 PER', stats: { per: 3 } }],
      3: [{ text: '+10% damage against Mutants', stats: { vsMutant: 0.1 } }],
      5: [{ text: 'See the other survivor’s power and cargo value before an encounter', hook: 'hunter_intel' }],
    },
  },
  medic: {
    id: 'medic',
    name: 'Medic',
    mark: { letter: '+', color: '#E38BA6', ink: '#14130F' },
    bonuses: {
      2: [{ text: '+10% healing', stats: { healing: 0.1 } }],
      3: [{ text: 'Heal 5% HP after every fight', stats: { postFightHeal: 0.05 } }],
      5: [{ text: 'Getting away from a fight restores 15% HP', hook: 'medic_retreat_heal' }],
    },
  },
  nightstalker: {
    id: 'nightstalker',
    name: 'Nightstalker',
    mark: { letter: 'N', color: '#7B7FB8', ink: '#14130F' },
    bonuses: {
      2: [{ text: '−10% chance to be detected', stats: { stealth: 0.1 } }],
      3: [{ text: '+15% ambush damage', stats: { ambush: 0.15 } }],
      5: [{ text: 'Following lasts 2 ticks longer, and the target gets no detection roll on the first tick', hook: 'nightstalker_tracking' }],
    },
  },
  military: {
    id: 'military',
    name: 'Military',
    mark: { letter: 'M', color: '#8A8F5C', ink: '#14130F' },
    bonuses: {
      2: [{ text: '+5% attack', stats: { attackPct: 0.05 } }],
      3: [{ text: '+10 defense', stats: { defense: 10 } }],
      5: [{ text: '+10% to all stats in Fort Halden and open-PvP zones', hook: 'military_zone' }],
    },
  },
  // 11. 10. 2026 (owner, BACKLOG #36): heavy armor of T3, defense and melee resistance. Plain numbers only (no hook): the
  // 2-piece as the Scrapper's defense one tier up, the 3-piece and the 5-piece melee resistance (with the 5-piece's defense
  // the whole set gives +12 defense and 10% melee resistance; melee resistance is situational and stays out of Power).
  foundry: {
    id: 'foundry',
    name: 'Foundry',
    mark: { letter: 'F', color: '#58A0A0', ink: '#14130F' },
    bonuses: {
      2: [{ text: '+6 defense', stats: { defense: 6 } }],
      3: [{ text: '5% less damage from melee hits', stats: { meleeResist: 0.05 } }],
      5: [{ text: '+6 defense and 5% less damage from melee hits', stats: { defense: 6, meleeResist: 0.05 } }],
    },
  },
};

export interface ActiveSet {
  id: SetId;
  name: string;
  /** Equipped pieces of this set (broken ones too, 7. 10. 2026). */
  count: number;
  /** Thresholds reached (2, 3, 5). */
  active: SetThreshold[];
}

export interface ActiveSetBonuses {
  sets: ActiveSet[];
  stats: Required<SetStatMods>;
  hooks: SetHookId[];
}

const ZERO: Required<SetStatMods> = { escape: 0, agi: 0, dodge: 0, capacity: 0, craftSpeed: 0, defense: 0, dismantleScrap: 0, healing: 0, postFightHeal: 0, stealth: 0, ambush: 0, attackPct: 0, repairDiscount: 0, expeditionTime: 0, per: 0, vsMutant: 0, meleeResist: 0 };

/**
 * Equipped pieces per set. A broken piece still counts (7. 10. 2026: only its own stats drop; with BROKEN_RULES off,
 * as before: it counts for nothing).
 */
export function setPieceCounts(equipment: Equipment): Partial<Record<SetId, number>> {
  const out: Partial<Record<SetId, number>> = {};
  for (const slot of ARMOR_SLOTS) {
    const it = equipment[slot];
    if (!it || !wornCounts(it)) continue;
    const setId = findItemTemplate(it.templateId)?.setId;
    if (setId) out[setId] = (out[setId] ?? 0) + 1;
  }
  return out;
}

/** Every set bonus the equipment switches on, summed into stats and hooks. */
export function getActiveSetBonuses(equipment: Equipment): ActiveSetBonuses {
  const counts = setPieceCounts(equipment);
  const stats = { ...ZERO };
  const hooks: SetHookId[] = [];
  const sets: ActiveSet[] = [];
  for (const id of SET_IDS) {
    const count = counts[id] ?? 0;
    if (!count) continue;
    const def = SETS[id];
    const active = SET_THRESHOLDS.filter((n) => count >= n);
    sets.push({ id, name: def.name, count, active });
    for (const n of active) {
      for (const b of def.bonuses[n]) {
        for (const [k, v] of Object.entries(b.stats ?? {}) as [keyof SetStatMods, number][]) stats[k] += v;
        if (b.hook) hooks.push(b.hook);
      }
    }
  }
  return { sets, stats, hooks };
}

/** "Drifter · 2: +5% escape chance · 3: +2 AGI · 5: …" for tooltips. */
export function setSummary(id: SetId): string {
  const def = SETS[id];
  return `${def.name} set · ${SET_THRESHOLDS.map((n) => `${n}: ${def.bonuses[n].map((b) => b.text).join(', ')}`).join(' · ')}`;
}

export const SET_SIZE = ARMOR_SLOTS.length;
